import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, LinearProgress, Button, TextField } from "@material-ui/core";
import { Club } from "api/clubs";
import { patchTransferOwnership } from "api/owner/settings";
import { useSnackbar } from "notistack";
import { getMembers, Member } from "api/admin/members";
import { Autocomplete } from "@material-ui/lab";
import i18n from "../i18n";

// prop types
interface TransferOwnershipDialogProps {
    club?: Club,
    show: boolean,
    onClose: () => void,
    onFetchUserProfile: () => void,
}

// transfer ownership dialog
const TransferOwnershipDialog = ({ club, show, onClose, onFetchUserProfile }: TransferOwnershipDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [isLoading, setLoading] = useState(false);
    const [members, setMembers] = useState<Member[]>([]);
    const [newOwner, setNewOwner] = useState<Member | null>(null);

    // get members, on mount
    const fetchMembers = async (query: string) => {
        setMembers((await getMembers({ clubId: club?.id, limit: 10, query })).data);
    };
    useEffect(() => { if (club) fetchMembers(""); }, [club]); // eslint-disable-line

    // execute action
    const execute = async () => {
        setLoading(true);
        try {
            await patchTransferOwnership({
                clubId: club!.id,
                userId: newOwner!.id,
            });
            onFetchUserProfile(); // refresh
            enqueueSnackbar(i18n.transferredOwnershipSuccessfully, { variant: "success" });
            onClose();
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // render
    return (
        <Dialog open={show} onClose={onClose}>
            <DialogTitle>{i18n.transferOwnership}</DialogTitle>
            <DialogContent dividers>
                <DialogContentText>
                    {i18n.transferOwnershipDesc}
                </DialogContentText>
                <Autocomplete
                    getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                    getOptionSelected={(option, value) => option.id === value.id}
                    options={members}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={i18n.newOwner}
                            placeholder={i18n.enterName}
                        />
                    )}
                    value={newOwner}
                    onChange={(e, v) => setNewOwner(v)}
                    onInputChange={(e, v, r) => (r === "input") && fetchMembers(v)}
                />
            </DialogContent>
            {isLoading && <LinearProgress />}
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    {i18n.cancel}
                </Button>
                <Button style={newOwner ? { color: "#d44336" } : {}} disabled={isLoading || !newOwner} onClick={execute}>
                    {i18n.transfer}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TransferOwnershipDialog;
